import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { PMDialog } from "../../utils/PMDialog";
import { EmployeePickerSheet } from "../hours/hourFormPickers";
import { AbsenceTypePickerSheet } from "../absence/absencePickers";
import { createAbsence, updateAbsence } from "../../../api/absence/absenece";
import { planGhostBtnClass, planPrimaryBtnClass } from "./projectPlanTheme";

/**
 * Compact admin-only absence create from Prosjektplan.
 * Prefills date_from when opened from a day column.
 */
export function PlanAbsenceDialog({
  open,
  onClose,
  initialDate = null,
  initialUser = null,
  onSaved,
}) {
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [absenceType, setAbsenceType] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [approveImmediately, setApproveImmediately] = useState(false);
  const [employeeOpen, setEmployeeOpen] = useState(false);
  const [typeOpen, setTypeOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    if (!open) return;
    const day = initialDate ? String(initialDate).substring(0, 10) : "";
    setUser(initialUser || null);
    setAbsenceType(null);
    setDateFrom(day);
    setDateTo(day);
    setApproveImmediately(false);
    setErrors([]);
    setSaving(false);
    setEmployeeOpen(false);
    setTypeOpen(false);
  }, [open, initialDate, initialUser]);

  const canSubmit =
    Boolean(user?.id) &&
    Boolean(absenceType?.id) &&
    Boolean(dateFrom) &&
    !saving;

  const handleSave = async () => {
    if (!canSubmit) return;
    const from = dateFrom;
    const to = dateTo || dateFrom;
    if (to < from) {
      setErrors([t("project.plan.absence.date_order_error")]);
      return;
    }

    setSaving(true);
    setErrors([]);
    try {
      const createResult = await createAbsence(
        {
          user_id: Number(user.id),
          absence_type_id: Number(absenceType.id),
          date_from: from,
          date_too: to,
        },
        { fields: "*" }
      );

      if (!createResult.success) {
        setErrors(createResult.errors || [t("project.plan.absence.save_error")]);
        setSaving(false);
        return;
      }

      const created = createResult.data;
      let approved = false;
      if (approveImmediately && created?.id) {
        const approveResult = await updateAbsence({
          id: created.id,
          accepted: true,
        });
        if (!approveResult.success) {
          toast.error(
            (approveResult.errors || []).join(", ") ||
              t("project.plan.absence.approve_error")
          );
          // Create succeeded; refresh plan but do not claim approval.
          onSaved?.(created);
          onClose?.();
          return;
        }
        approved = true;
      }

      toast.success(
        approveImmediately && approved
          ? t("project.plan.absence.toast_created_approved")
          : t("project.plan.absence.toast_created")
      );
      onSaved?.(created);
      onClose?.();
    } catch (err) {
      setErrors([err?.message || t("project.plan.absence.save_error")]);
    } finally {
      setSaving(false);
    }
  };

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      title={t("project.plan.absence.title")}
      size="md"
    >
      <p className="mb-4 text-sm text-[#6B7682]">
        {t("project.plan.absence.help")}
      </p>

      {errors.length > 0 ? (
        <div className="mb-4 rounded-[10px] border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {errors.map((msg) => (
            <div key={msg}>{msg}</div>
          ))}
        </div>
      ) : null}

      <div className="flex flex-col gap-4">
        <div>
          <label className="mb-1.5 block text-[13px] font-semibold text-[#334155]">
            {t("project.plan.absence.employee")}
          </label>
          <button
            type="button"
            onClick={() => setEmployeeOpen(true)}
            className="flex w-full items-center justify-between rounded-[14px] border-[1.5px] border-[#E2E8F0] bg-white px-3.5 py-3 text-left"
          >
            <span
              className={`text-[15px] font-semibold ${
                user ? "text-[#0F172A]" : "text-[#94A3B8]"
              }`}
            >
              {user?.displayTitle || user?.name || t("project.plan.absence.pick_employee")}
            </span>
            <ChevronDownIcon className="h-4 w-4 text-[#94A3B8]" />
          </button>
          <EmployeePickerSheet
            open={employeeOpen}
            onClose={() => setEmployeeOpen(false)}
            selectedId={user?.id}
            onSelect={(obj) => setUser(obj || null)}
          />
        </div>

        <div>
          <label className="mb-1.5 block text-[13px] font-semibold text-[#334155]">
            {t("project.plan.absence.type")}
          </label>
          <button
            type="button"
            onClick={() => setTypeOpen(true)}
            disabled={!user?.id}
            className="flex w-full items-center justify-between rounded-[14px] border-[1.5px] border-[#E2E8F0] bg-white px-3.5 py-3 text-left disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span
              className={`text-[15px] font-semibold ${
                absenceType ? "text-[#0F172A]" : "text-[#94A3B8]"
              }`}
            >
              {absenceType?.displayTitle ||
                absenceType?.title ||
                t("project.plan.absence.pick_type")}
            </span>
            <ChevronDownIcon className="h-4 w-4 text-[#94A3B8]" />
          </button>
          <AbsenceTypePickerSheet
            open={typeOpen}
            onClose={() => setTypeOpen(false)}
            userId={user?.id}
            selectedId={absenceType?.id}
            onSelect={(type) => setAbsenceType(type || null)}
          />
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1.5 block text-[13px] font-semibold text-[#334155]">
              {t("project.plan.absence.date_from")}
            </label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                if (!dateTo || dateTo < e.target.value) setDateTo(e.target.value);
              }}
              className="w-full rounded-[14px] border-[1.5px] border-[#E2E8F0] bg-white px-3.5 py-3 text-[15px] text-[#0F172A]"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-[13px] font-semibold text-[#334155]">
              {t("project.plan.absence.date_to")}
            </label>
            <input
              type="date"
              value={dateTo}
              min={dateFrom || undefined}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full rounded-[14px] border-[1.5px] border-[#E2E8F0] bg-white px-3.5 py-3 text-[15px] text-[#0F172A]"
            />
          </div>
        </div>

        <label className="flex cursor-pointer items-start gap-3 rounded-[12px] border border-[#E5E9F0] bg-[#F8FAFC] px-3.5 py-3">
          <input
            type="checkbox"
            checked={approveImmediately}
            onChange={(e) => setApproveImmediately(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-[#CBD5E1] text-[#2B6CFF] focus:ring-[#2B6CFF]"
          />
          <span>
            <span className="block text-[14px] font-semibold text-[#0F172A]">
              {t("project.plan.absence.approve_now")}
            </span>
            <span className="mt-0.5 block text-[12.5px] text-[#6B7682]">
              {t("project.plan.absence.approve_now_help")}
            </span>
          </span>
        </label>
      </div>

      <div className="mt-6 flex flex-wrap justify-end gap-2">
        <button type="button" onClick={onClose} className={planGhostBtnClass} disabled={saving}>
          {t("Cancel")}
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={!canSubmit}
          className={planPrimaryBtnClass}
        >
          {saving ? t("Loading") : t("project.plan.absence.save")}
        </button>
      </div>
    </PMDialog>
  );
}
